import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { CreateReportPayload, ExportFormat, ReportItem, ReportListResponse } from "entities/reports";

import { SilentMeta } from "store/silent";

type ReportsState = {
  list: ReportItem[];
  meta: { total: number; page: number; page_size: number };
  lastQuery: string;
  hasReports: boolean;
  isListLoading: boolean;
  listLoaded: boolean;
  isCreating: boolean;
  isRemoving: boolean;
};

const initialState: ReportsState = {
  list: [],
  meta: { total: 0, page: 1, page_size: 20 },
  lastQuery: "?page=1&page_size=20",
  hasReports: false,
  isListLoading: false,
  listLoaded: false,
  isCreating: false,
  isRemoving: false,
};

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    getReportListRequest: {
      reducer(state, action: PayloadAction<string, string, SilentMeta>) {
        state.lastQuery = action.payload;
        if (!action.meta?.silent) state.isListLoading = true;
      },
      prepare: (query: string, meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getReportListSuccess(state, action: PayloadAction<ReportListResponse>) {
      state.list = action.payload.items;
      state.meta = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
      };
      state.hasReports = action.payload.has_reports;
      state.isListLoading = false;
      state.listLoaded = true;
    },
    getReportListFailure(state) {
      state.isListLoading = false;
      state.listLoaded = true;
    },
    createReportRequest(state, action: PayloadAction<CreateReportPayload>) {
      void action.payload;
      state.isCreating = true;
    },
    createReportSuccess(state) {
      state.isCreating = false;
    },
    createReportFailure(state) {
      state.isCreating = false;
    },
    downloadReportRequest(state, action: PayloadAction<{ id: number; name: string; format: ExportFormat }>) {
      void action.payload;
    },
    removeReportRequest(state, action: PayloadAction<number>) {
      void action.payload;
      state.isRemoving = true;
    },
    removeReportSuccess(state) {
      state.isRemoving = false;
    },
    removeReportFailure(state) {
      state.isRemoving = false;
    },
    retryReportRequest(state, action: PayloadAction<number>) {
      void action.payload;
    },
  },
});

export const {
  createReportFailure,
  createReportRequest,
  createReportSuccess,
  downloadReportRequest,
  getReportListFailure,
  getReportListRequest,
  getReportListSuccess,
  removeReportFailure,
  removeReportRequest,
  removeReportSuccess,
  retryReportRequest,
} = reportsSlice.actions;

export default reportsSlice.reducer;
